/**
 * /pay/:unitId/:linkId/complete?pid= – where payers land after the provider.
 * Confirms the payment, polling with backoff while the bank settles (mainly SlickPay).
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, Gift, Loader2, XCircle } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import type { PaymentLinkConfirmResponse } from '@masonic-tools/shared-types';
import { confirmPaymentLinkPayment } from '@/lib/payment-links-api';
import { formatPence } from '@/lib/unit-payment-links';
import { ReferenceChip } from '@/components/payment-links/PaymentLinkStatusBadge';
import { PublicPageShell, payerFormStorageKey } from './PublicPaymentLinkPage';

/** Wait times between checks, ~60s in total. */
export const CONFIRM_BACKOFF_MS = [0, 2000, 3000, 5000, 5000, 8000, 10000, 12000, 15000];

const BANK_FAILURE_MESSAGES: Record<string, string> = {
  denied: 'Payment cancelled. No money was taken from your account.',
  rejected: 'Your bank rejected this payment.',
  funds: 'The payment failed because of insufficient funds.',
  notFound: 'Your bank could not find this payment.',
  error: 'Something went wrong with your bank.',
};

type View = 'confirming' | 'paid' | 'processing' | 'failed';

export default function PaymentLinkCompletePage() {
  const { unitId = '', linkId = '' } = useParams<{ unitId: string; linkId: string }>();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('pid') || '';
  const bankStatus = searchParams.get('bank');
  const [view, setView] = useState<View>(paymentId ? 'confirming' : 'failed');
  const [result, setResult] = useState<PaymentLinkConfirmResponse | null>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!paymentId) return;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout>;

    const check = async () => {
      if (cancelled.current) return;
      try {
        const r = await confirmPaymentLinkPayment({ unitId, linkId, paymentId });
        if (cancelled.current) return;
        setResult(r);
        if (r.status === 'paid') {
          setView('paid');
          try {
            sessionStorage.removeItem(payerFormStorageKey(linkId));
          } catch {
            /* ignore */
          }
          return;
        }
        if (r.status === 'failed' || r.status === 'cancelled') {
          setView('failed');
          return;
        }
      } catch {
        /* network blip: keep trying */
      }
      attempt++;
      // The bank already said no: give the backend a couple of tries, then show the failure.
      if (bankStatus && bankStatus !== 'success' && attempt >= 3) {
        setView('failed');
        return;
      }
      if (attempt >= CONFIRM_BACKOFF_MS.length) {
        setView('processing');
        return;
      }
      timer = setTimeout(check, CONFIRM_BACKOFF_MS[attempt]);
    };
    void check();
    return () => {
      cancelled.current = true;
      clearTimeout(timer);
    };
  }, [unitId, linkId, paymentId, bankStatus]);

  const backToLink = `/pay/${encodeURIComponent(unitId)}/${encodeURIComponent(linkId)}`;

  return (
    <PublicPageShell>
      <div className="rounded-xl border border-border bg-card p-6 text-center shadow-sm sm:p-8" aria-live="polite">
        {view === 'confirming' ? (
          <>
            <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
            <h1 className="text-xl font-semibold">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-muted-foreground">This usually takes a few seconds. Please keep this page open.</p>
          </>
        ) : null}

        {view === 'paid' && result ? (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-green-600 animate-in zoom-in-50 duration-500" />
            <h1 className="text-2xl font-bold">Thank you!</h1>
            <p className="mt-1 text-muted-foreground">
              Your payment to {result.unitName} for <strong className="text-foreground">{result.linkTitle}</strong> was successful.
            </p>
            <p className="mt-4 text-4xl font-bold tabular-nums">{formatPence(result.totalChargedPence)}</p>
            {result.feeMode === 'on_top' && result.processingFeePence > 0 ? (
              <p className="text-xs text-muted-foreground">
                {formatPence(result.amountPence)} + {formatPence(result.processingFeePence)} processing fee
              </p>
            ) : null}
            {result.reference ? (
              <p className="mt-3 text-sm text-muted-foreground">
                Reference <ReferenceChip reference={result.reference} />
              </p>
            ) : null}
            {result.thankYouMessage ? <blockquote className="mt-5 rounded-lg bg-muted p-4 text-sm italic">“{result.thankYouMessage}”</blockquote> : null}
            <ul className="mt-5 space-y-1 text-sm text-muted-foreground">
              {result.receiptEmail ? <li>A receipt has been sent to {result.receiptEmail}.</li> : null}
              {result.giftAid ? (
                <li className="flex items-center justify-center gap-1">
                  <Gift className="h-4 w-4 text-primary" /> Your Gift Aid declaration has been recorded.
                </li>
              ) : null}
            </ul>
            <Link to={backToLink} className={`${buttonVariants({ variant: 'outline' })} mt-6 w-full`}>
              Make another payment
            </Link>
          </>
        ) : null}

        {view === 'processing' ? (
          <>
            <Clock className="mx-auto mb-4 h-12 w-12 text-amber-500" />
            <h1 className="text-xl font-semibold">Your bank is still confirming</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              You can close this page.{result?.receiptEmail ? ` We'll email your receipt to ${result.receiptEmail} once it's confirmed.` : ' The payment will be recorded once your bank confirms it.'}
            </p>
          </>
        ) : null}

        {view === 'failed' ? (
          <>
            <XCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
            <h1 className="text-xl font-semibold">Payment not completed</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {(bankStatus && BANK_FAILURE_MESSAGES[bankStatus]) ||
                result?.closedReason ||
                (paymentId ? 'The payment was cancelled or did not go through. No money was taken.' : 'We could not find this payment.')}
            </p>
            <Link to={backToLink} className={`${buttonVariants()} mt-6 w-full`}>
              Try again
            </Link>
          </>
        ) : null}
      </div>
    </PublicPageShell>
  );
}
